import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../utils/Url";
import { Sidebar } from "../sidebar/Sidebar";
import { Header } from "../header/Header";
import { toast } from "react-toastify";

export default function UserProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [token, setToken] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    setToken(token);
  }, [navigate]);

  const fetchUser = async () => {
    const token = sessionStorage.getItem("token");
    try {
      const { data } = await axios.get(`${API_URL}/api/user/get/user/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.success) {
        setUser(data.user);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      toast.error("Failed to load user credentials pipeline.");
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchUser();
  }, [id, token]);

  const handleChangeStatus = async (userId, status) => {
    const token = sessionStorage.getItem("token");
    try {
      setUpdatingStatus(true);
      const res = await axios.put(
        `${API_URL}/api/user/update/status/${userId}`,
        { userStatus: status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data) {
        toast.success(`Account status updated to ${status}`);
        fetchUser();
      }
    } catch (err) {
      console.error(err.response?.data?.message || err.message);
      toast.error("Status synchronization handshake failed.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            Parsing node schema details...
          </span>
        </div>
      </div>
    );
  }

  // Pure dynamic status context styling variables configuration maps
  const statusColors = {
    pending: {
      bg: "bg-warning-subtle text-warning border-warning-subtle",
      dot: "bg-warning",
    },
    approved: {
      bg: "bg-success-subtle text-success border-success-subtle",
      dot: "bg-success",
    },
    rejected: {
      bg: "bg-danger-subtle text-danger border-danger-subtle",
      dot: "bg-danger",
    },
    blocked: {
      bg: "bg-dark-subtle text-dark border-dark-subtle",
      dot: "bg-dark",
    },
  };
  const activeStyle = statusColors[user?.userStatus] || {
    bg: "bg-secondary-subtle text-secondary",
    dot: "bg-secondary",
  };

  return (
    <div className="bg-light min-vh-100">
      <Header />
      <Sidebar />

      <div
        className="main-content mt-5 px-4 py-5"
        style={{ marginLeft: "260px", paddingTop: "40px" }}
      >
        {/* TOP INTERACTIVE CONTROL PANEL BAR */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3 bg-white p-3 rounded-4 shadow-sm border border-light">
          <div>
            <nav aria-label="breadcrumb">
              <ol
                className="breadcrumb mb-1 text-uppercase font-monospace text-xs"
                style={{ fontSize: "11px", trackingSpacing: "1px" }}
              >
                <li className="breadcrumb-item">
                  <a
                    href="/dashboard"
                    className="text-decoration-none text-muted"
                  >
                    Admin
                  </a>
                </li>
                <li
                  className="breadcrumb-item active text-primary"
                  aria-current="page"
                >
                  Profile Registry
                </li>
              </ol>
            </nav>
            <h3 className="fw-black text-slate-950 m-0 tracking-tight">
              Account Audit Workspace
            </h3>
          </div>

          <div className="d-flex align-items-center gap-3 w-100 w-md-auto justify-content-md-end">
            <button className="btn btn-sm btn-light border fw-semibold text-secondary px-3 py-2 rounded-3 hover-shadow">
              💳 Change Bar ID
            </button>

            {/* Elegant Status Controller Integration Selector */}
            <div className="d-flex align-items-center gap-2 bg-light p-1.5 rounded-3 border">
              <span
                className={`badge border ${activeStyle.bg} px-2.5 py-1.5 rounded-2 d-flex align-items-center gap-1.5 text-capitalize small fw-bold`}
              >
                <span
                  className={`d-inline-block rounded-circle ${activeStyle.dot}`}
                  style={{ width: "6px", height: "6px" }}
                ></span>
                {user?.userStatus}
              </span>
              <select
                className="form-select form-select-sm border-0 bg-transparent fw-bold text-dark cursor-pointer"
                style={{ width: "130px", focusShadow: "none" }}
                value={user?.userStatus}
                disabled={updatingStatus}
                onChange={(e) => handleChangeStatus(user?._id, e.target.value)}
              >
                <option value="pending">Set Pending</option>
                <option value="approved">Approve User</option>
                <option value="rejected">Reject Route</option>
                <option value="blocked">Block Account</option>
              </select>
            </div>
          </div>
        </div>

        {/* PROFILE HERO ROW CARD BANNER */}
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4 bg-white border border-light">
          <div
            className=" p-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #dcdafa 0%, #e4e3ec 100%)",
              height: "110px",
            }}
          ></div>
          <div className="card-body px-4 pb-4 pt-0 position-relative">
            <div className="row align-items-end" style={{ marginTop: "-55px" }}>
              <div className="col-12 col-md-auto text-center text-md-start mb-3 mb-md-0">
                <img
                  src={
                    user?.userImages?.[user?.userImages.length - 1]?.picUrl
                      ? `${API_URL}${user.userImages[user.userImages.length - 1].picUrl}`
                      : "https://cdn-icons-png.flaticon.com/512/3177/3177440.png"
                  }
                  className="rounded-circle border border-4 border-white shadow object-fit-cover bg-white"
                  width="120"
                  height="120"
                  alt={user?.name}
                />
              </div>
              <div className="col text-center text-md-start mb-3 mb-md-0 ps-md-4">
                <div className="d-flex flex-column flex-sm-row align-items-center gap-2 mb-1 justify-content-center justify-content-md-start">
                  <h3 className="fw-black text-slate-900 m-0">{user?.name}</h3>
                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill font-monospace text-[10px] px-2 py-0.5">
                    ID: {user?._id?.substring(0, 8).toUpperCase()}
                  </span>
                </div>
                <p className="text-muted small mb-0 d-flex flex-wrap gap-3 justify-content-center justify-content-md-start">
                  <span>✉️ {user?.email}</span>
                  <span>📞 +{user?.mobile}</span>
                  <span> {user?.MatrimonyFor}</span>
                </p>
              </div>
              <div className="col-12 col-md-auto text-center text-md-end">
                <button
                  className="btn btn-dark fw-bold text-uppercase px-4 py-2 rounded-3 text-xs tracking-wider border-0"
                  style={{ fontSize: "11px", background: "#4f46e5" }}
                  onClick={() => setShowGallery(true)}
                >
                  🖼️ View Albums
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* STRUCTURE ANALYTICS CONTENT SPLIT GRID */}
        <div className="row g-4">
          <DetailCard title="Personal Parameters" icon="👤">
            <Detail label="Gender Structure" value={user?.gender} />
            <Detail label="Date of Birth" value={user?.dob} />
            <Detail label="Mother Tongue" value={user?.motherTongue} />
            <Detail label="Marital Status" value={user?.maritalStatus} />
            <Detail label="Physical Height" value={user?.height} />
          </DetailCard>

          <DetailCard title="Religion & Horoscope" icon="✨">
            <Detail label="Religion Node" value={user?.religion} />
            <Detail label="Community Caste" value={user?.caste} />
            <Detail label="Zodiac / Rashi" value={user?.Rashi} />
            <Detail label="Manglik Status" value={user?.Manglik} />
          </DetailCard>

          <DetailCard title="Geographic Coordinates" icon="📍">
            <Detail label="Current City" value={user?.city} />
            <Detail label="Registered State" value={user?.state} />
            <Detail label="Country Origin" value={user?.country} />
          </DetailCard>

          <DetailCard title="Professional Telemetry" icon="💼">
            <Detail label="Education Tier" value={user?.education} />
            <Detail label="Profession Stream" value={user?.profession} />
            <Detail label="Annual Income Matrix" value={user?.Income} />
          </DetailCard>

          <DetailCard
            title="Family Ancestry Profile"
            icon="🏠"
            isFullWidth={true}
          >
            <div className="row">
              <div className="col-12 col-md-4">
                <Detail
                  label="Family Status Level"
                  value={user?.FamilyStatus}
                />
              </div>
              <div className="col-12 col-md-4">
                <Detail label="Brothers Count Logs" value={user?.Brothers} />
              </div>
              <div className="col-12 col-md-4">
                <Detail label="Sisters Count Logs" value={user?.Sisters} />
              </div>
            </div>
          </DetailCard>
        </div>

        {/* SYSTEM BIO INTRODUCTION */}
        {user?.briefintroduction && (
          <div className="card border-0 shadow-sm rounded-4 mt-4 bg-white border border-light">
            <div className="card-body p-4">
              <h5 className="fw-black text-slate-900 mb-3 d-flex align-items-center gap-2">
                <span className="fs-5">📝</span> Statement Bio Overview
              </h5>
              <p
                className="text-secondary mb-0 lh-base font-monospace bg-light p-3 rounded-3 border border-gray-100"
                style={{ fontSize: "13px" }}
              >
                "{user?.briefintroduction}"
              </p>
            </div>
          </div>
        )}

        {/* VERIFICATION CRITICAL LEGAL ID PROOF */}
        {user?.enrollIdImg && (
          <div className="card border-0 shadow-sm rounded-4 mt-4 bg-white border border-light">
            <div className="card-body p-4">
              <h5 className="fw-black text-danger mb-3 d-flex align-items-center gap-2">
                <span className="fs-5">🛡️</span> Government Identity
                Verification Node
              </h5>
              <div
                className="position-relative overflow-hidden rounded-3 border max-w-sm shadow-xs hover-shadow transition-all"
                style={{ maxWidth: "380px" }}
              >
                <img
                  src={user?.enrollIdImg}
                  alt="ID Proof"
                  className="img-fluid object-fit-contain w-100 bg-light"
                  style={{ maxHeight: "240px" }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PREMIUM OVERLAY LAYER BLUR MODAL GALLERY */}
      {showGallery && (
        <div
          className="modal show d-block fade-in bg-slate-950/60 backdrop-blur-xs"
          tabIndex="-1"
          style={{ zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-bottom bg-light px-4 py-3">
                <h5 className="modal-title fw-black text-slate-900">
                  Media Vault: {user?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowGallery(false)}
                ></button>
              </div>
              <div
                className="modal-body p-4"
                style={{ maxHeight: "65vh", overflowY: "auto" }}
              >
                <div className="row g-3">
                  {user?.userImages?.length > 0 ? (
                    user.userImages.map((img, index) => (
                      <div className="col-12 col-sm-6 col-md-4" key={index}>
                        <div
                          className="position-relative overflow-hidden rounded-3 shadow-sm border border-gray-100 group transition-all"
                          style={{ height: "200px" }}
                        >
                          <img
                            src={`${API_URL}${img.picUrl}`}
                            className="w-100 h-100 object-fit-cover transition-transform duration-300"
                            alt="Gallery Node"
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted py-5 w-100">
                      No media parameters matching inside storage system.
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer bg-light p-2">
                <button
                  type="button"
                  className="btn btn-secondary text-xs uppercase font-bold tracking-wide rounded-2 px-4 py-2"
                  onClick={() => setShowGallery(false)}
                >
                  Close Vault
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DetailCard = ({ title, icon, children, isFullWidth = false }) => (
  <div className={isFullWidth ? "col-12" : "col-12 col-md-6"}>
    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white border border-light">
      <div className="card-body p-4">
        <h5 className="fw-black text-slate-900 mb-3 border-bottom pb-2 d-flex align-items-center gap-2">
          <span className="fs-5">{icon}</span> {title}
        </h5>
        <div className="d-flex flex-column gap-2.5">{children}</div>
      </div>
    </div>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center py-1 border-bottom border-dashed border-gray-100">
    <span className="text-muted small fw-medium">{label}</span>
    <span className="text-dark fw-bold small text-end">{value || "—"}</span>
  </div>
);
